
import { faArrowRightLong, faRoute } from '@fortawesome/pro-regular-svg-icons'
import { faDog, faLocationArrow, faLocationXmark, faPawSimple } from '@fortawesome/pro-solid-svg-icons'
import distance from '@turf/distance'
import Vue from 'vue'

// We are manually importing our component so we can render it off-document and grab its html for a map marker
import MapIcon from '~/components/elements/geo/map/marker/MapIcon.vue'
import { getCurrentPosition } from '~/lib/geo/currentPosition'

const IconInstance = Vue.extend(MapIcon)

export default {
  name: 'MapFindPaddock',

  props: {
    paddocks: {
      type: Array,
      default: () => []
    },

    isFullPage: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isMapReady: false,
      paddockEntranceGeoJson: {
        type: 'FeatureCollection',
        features: []
      },

      address: {
        fetchPostcodeApi: new this.$ApiModel(),
        postcode: null,
        error: null,
        hasLocation: false,
        addressCoordinates: []
      },

      allPaddockBounds: null,
      isFetchingLocation: false,
      paddockMarkers: [],
      startMarker: null,
      faLocationArrow,
      faLocationXmark,
      faRoute,
      faPawSimple,
      faArrowRightLong
    }
  },

  computed: {
    compMap() {
      return this.$refs.paddockMap?.map
    },

    compPaddocksInDistanceOrder() {
      if (this.address.addressCoordinates.length === 0) {
        if (!this.isFullPage) {
          return this.paddocks
        } else {
          return []
        }
      }

      const paddocks = this.paddocks
        .map(paddock => {
          const distanceMiles = distance(this.address.addressCoordinates, paddock.entranceCoordinates, {
            units: 'miles'
          })

          return {
            ...paddock,
            distanceMiles: Math.round(distanceMiles)
          }
        })
        .sort((a, b) => a.distanceMiles - b.distanceMiles)

      if (!this.isFullPage) {
        return paddocks.slice(0, 3)
      }

      return paddocks
    },

    compClosestPaddockId() {
      if (this.compPaddocksInDistanceOrder.length === 0) {
        return null
      }

      return this.compPaddocksInDistanceOrder[0].id
    }
  },

  methods: {
    async onMapReady() {
      this.isMapReady = true

      await this.$nextTick()

      this.addPaddockMarkers()
    },

    selectPaddock(paddock) {
      this.$emit('selected', paddock)

      this.$analytics.addEvent('Selected paddock from paddock finder', paddock.id)
    },

    findOutMorePaddock(paddock) {
      this.$emit('findOutMore', paddock)
    },

    bookPaddock(paddock) {
      this.$emit('book', paddock)
    },

    async reset() {
      this.$log.debug('Resetting', this.allPaddockBounds)

      this.address.addressCoordinates = []

      this.startMarker.remove()

      try {
        this.compMap.fitBounds(this.allPaddockBounds, {
          padding: { top: 50, bottom: 50, left: 70, right: 70 }
        })
      } catch (error) {
        this.$log.debug('Error fitting bounds in reset map', error)

        try {
          setTimeout(() => {
            this.compMap.fitBounds(this.allPaddockBounds)
          }, 1000)
        } catch (newError) {
          this.$log.debug('Error #2 fitting bounds in reset map', error)
        }
      }

      await this.$nextTick()

      this.startMarker = null
    },

    setStartMarker(coordinates) {
      if (!this.startMarker) {
        const markerElement = document.createElement('div')

        const iconComponent = new IconInstance({
          propsData: {
            icon: faDog,
            wrapperClass: 'text-4xl',
            color: 'blue'
          }
        }).$mount().$el

        markerElement.className = `dog-marker-js`
        markerElement.innerHTML = iconComponent.outerHTML

        this.startMarker = new window.mapboxgl.Marker(markerElement, {
          anchor: 'bottom'
        })

        this.startMarker.setLngLat(coordinates).addTo(this.compMap)
      } else {
        this.startMarker.setLngLat(coordinates)
      }
    },

    addPaddockMarkers() {
      let haveBoundsBeenExtended = false
      const bounds = new window.mapboxgl.LngLatBounds()

      this.paddocks.forEach(paddock => {
        this.addPaddockMarker(paddock.entranceCoordinates, paddock)
        this.addPaddockField(paddock.fieldPolygon, paddock)

        this.paddockEntranceGeoJson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: paddock.entranceCoordinates
          },
          properties: {
            id: paddock.id,
            name: paddock.name
          }
        })

        if (
          paddock.entranceCoordinates &&
          Array.isArray(paddock.entranceCoordinates) &&
          paddock.entranceCoordinates.length === 2
        ) {
          bounds.extend(paddock.entranceCoordinates)
          haveBoundsBeenExtended = true
        } else {
          this.$log.error('Paddock has no entrance coordinates', paddock)
        }
      })

      this.compMap.addSource('entrance-markers', {
        type: 'geojson',
        data: this.paddockEntranceGeoJson
      })

      this.compMap.addLayer({
        id: 'poi-labels',
        type: 'symbol',
        source: 'entrance-markers',
        layout: {
          'text-field': ['get', 'name'],
          'text-variable-anchor': ['top', 'top-left'],
          'text-radial-offset': 0.5,
          'text-justify': 'auto',
          'text-size': 15,
          'text-letter-spacing': 0.05
        },
        paint: {
          'text-color': '#000'
        }
      })

      this.allPaddockBounds = bounds

      if (haveBoundsBeenExtended) {
        setTimeout(() => {
          try {
            this.compMap.fitBounds(bounds, {
              padding: { top: 50, bottom: 50, left: 70, right: 70 },
              animate: false
            })
          } catch (error) {
            this.$log.debug('Error fitting bounds in adding markers map', error)

            try {
              setTimeout(() => {
                this.compMap.fitBounds(bounds)
              }, 1000)
            } catch (newError) {
              this.$log.debug('Error #2 fitting bounds in adding markers map', newError)
            }
          }
        }, 200)
      }
    },

    addPaddockMarker(coordinates, paddock) {
      const markerElement = document.createElement('div')

      const iconComponent = new IconInstance({
        propsData: {
          icon: faPawSimple,
          wrapperClass: 'text-4xl',
          color: 'primary'
        }
      }).$mount().$el

      markerElement.className = `map-marker-js map-marker-${paddock.id}`
      markerElement.innerHTML = iconComponent.outerHTML

      const newMarker = {
        marker: new window.mapboxgl.Marker(markerElement, {
          anchor: 'bottom'
        }),

        paddock
      }

      newMarker.marker.setLngLat(coordinates).addTo(this.compMap)

      newMarker.marker.getElement().addEventListener('click', () => {
        this.selectPaddock(paddock)
      })

      this.paddockMarkers.push(newMarker)
    },

    addPaddockField(geoJson, paddock) {
      if (!geoJson) {
        return false
      }

      this.compMap.addSource(`field-${paddock.id}`, {
        type: 'geojson',
        data: geoJson
      })

      this.compMap.addLayer({
        id: `field-${paddock.id}`,
        type: 'fill',
        source: `field-${paddock.id}`,
        paint: {
          'fill-color': '#454659',
          'fill-opacity': 0.3
        }
      })

      this.compMap.addLayer({
        id: `field-outline-${paddock.id}`,
        type: 'line',
        source: `field-${paddock.id}`,
        paint: {
          'line-color': '#fff',
          'line-width': 2
        }
      })
    },

    async setStartingPosition(coordinates) {
      this.address.addressCoordinates = coordinates

      this.setStartMarker(this.address.addressCoordinates)

      const bounds = new window.mapboxgl.LngLatBounds()

      this.address.hasLocation = true

      bounds.extend(coordinates)
      bounds.extend(this.compPaddocksInDistanceOrder[0].entranceCoordinates)

      this.compMap.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 70, right: 70 },
        maxZoom: 10
      })

      setTimeout(() => {
        const iconComponent = new IconInstance({
          propsData: {
            icon: faPawSimple,
            wrapperClass: 'text-4xl',
            color: 'secondary'
          }
        }).$mount().$el

        const closestMarker = document.getElementsByClassName(`map-marker-${this.compClosestPaddockId}`)[0]

        if (closestMarker) {
          closestMarker.innerHTML = iconComponent.outerHTML
        }
      }, 500)

      await this.$nextTick()

      this.compMap.resize()
    },

    async fetchCurrentLocation() {
      this.isFetchingLocation = true

      try {
        const position = await getCurrentPosition(false)

        this.setStartingPosition([position.coords.longitude, position.coords.latitude])

        this.$analytics.addEvent('Fetched geo location')
      } catch (error) {
        console.log('Error fetching position', error)

        this.$notify.error('Could not fetch your location, do you have it enabled?')
      }

      this.isFetchingLocation = false
    },

    async lookupPostcode() {
      this.address.error = null

      if (!this.address.postcode) {
        this.address.error = 'Please enter a postcode or click find me'
        return false
      }

      try {
        this.$analytics.addEvent('Postcode lookup')

        await this.$api.geo(this.address.fetchPostcodeApi).lookupPostcode(this.address.postcode)

        const coordinates = this.address.fetchPostcodeApi.response.data?.coordinates

        if (coordinates) {
          this.setStartingPosition(coordinates)
        } else {
          throw new Error('No coordinates returned')
        }
      } catch (error) {
        this.address.hasLocation = false
        this.$log.debug('Error fetching postcode', error)

        this.address.error = "We couldn't find that postcode, please check and try again"
      }
    }
  }
}
